
import { useState } from 'react';

export default function QualificationPermit() {
  const [activeTab, setActiveTab] = useState('overview');
  const [selectedGuide, setSelectedGuide] = useState<string | null>(null);

  const permitTypes = [
    {
      id: 'food-business',
      title: '食品经营许可证',
      description: '从事餐饮服务、食品销售等业务的必备证件',
      icon: 'ri-restaurant-line',
      timeframe: '20个工作日',
      price: '免费（政府服务）',
      scope: ['餐饮服务', '食品销售', '网络食品销售', '食品自动售货'],
      conditions: ['营业执照', '经营场所', '设备设施', '管理制度', '健康证']
    },
    {
      id: 'medical-device',
      title: '医疗器械经营许可证',
      description: '经营第二、三类医疗器械的资质证明',
      icon: 'ri-health-book-line',
      timeframe: '20个工作日',
      price: '免费（政府服务）',
      scope: ['第二类器械备案', '第三类器械许可', '医疗设备销售', '器械维修'],
      conditions: ['营业执照', '经营场所', '质量管理', '专业人员', '信息系统']
    },
    {
      id: 'import-export',
      title: '进出口经营权备案',
      description: '企业开展进出口贸易业务的资质备案',
      icon: 'ri-ship-line',
      timeframe: '7-12个工作日',
      price: '免费（政府服务）',
      scope: ['货物进出口', '技术进出口', '对外贸易', '海关报关'],
      conditions: ['营业执照', '银行开户', '电子口岸', '外汇登记', '海关备案']
    },
    {
      id: 'prepackaged-food',
      title: '预包装食品经营备案',
      description: '仅销售预包装食品的经营备案（由许可改为备案）',
      icon: 'ri-shopping-bag-line',
      timeframe: '3-5个工作日',
      price: '免费（政府服务）',
      scope: ['预包装食品销售', '网络食品销售', '食品批发', '食品零售'],
      conditions: ['营业执照', '经营场所', '食品安全员', '备案申请', '公章']
    },
    {
      id: 'construction-qualification',
      title: '建筑业资质',
      description: '从事建筑施工活动的企业资质证书',
      icon: 'ri-building-4-line',
      timeframe: '45个工作日',
      price: '3000-10000元',
      scope: ['施工总承包', '专业承包', '施工劳务', '监理资质'],
      conditions: ['注册资本', '专业人员', '业绩要求', '设备条件', '管理体系']
    },
    {
      id: 'transport-license',
      title: '道路运输经营许可证',
      description: '从事道路货物运输和客运服务的许可',
      icon: 'ri-truck-line',
      timeframe: '15个工作日',
      price: '200-500元',
      scope: ['货物运输', '客运服务', '危险品运输', '出租车经营'],
      conditions: ['车辆条件', '驾驶员资格', '安全管理', '经营场所', '资金保证']
    },
    {
      id: 'education-license',
      title: '民办学校办学许可证',
      description: '举办民办教育机构的资质证明',
      icon: 'ri-school-line',
      timeframe: '60个工作日',
      price: '免费（政府服务）',
      scope: ['学历教育', '非学历教育', '职业培训', '艺术培训'],
      conditions: ['办学场所', '师资队伍', '教学设备', '办学资金', '管理制度']
    },
    {
      id: 'internet-culture',
      title: '网络文化经营许可证',
      description: '从事网络文化产品和服务的经营许可',
      icon: 'ri-computer-line',
      timeframe: '20个工作日',
      price: '1000-3000元',
      scope: ['网络游戏', '网络直播', '网络音乐', '网络表演'],
      conditions: ['注册资本', '专业人员', '技术设备', '内容管理', '安全措施']
    }
  ];

  const applicationProcess = [
    {
      step: '1',
      title: '条件核查',
      description: '确认企业是否满足申请资质的基本条件',
      icon: 'ri-check-double-line',
      details: ['营业执照审核', '经营范围确认', '场所条件评估', '人员资质检查']
    },
    {
      step: '2',
      title: '材料准备',
      description: '收集整理申请所需的全部材料',
      icon: 'ri-file-list-3-line',
      details: ['申请表填写', '证件材料复印', '专项材料准备', '材料真实性核实']
    },
    {
      step: '3',
      title: '网上申报',
      description: '通过政务服务网提交申请材料',
      icon: 'ri-upload-cloud-line',
      details: ['在线填报信息', '上传电子材料', '缴纳相关费用', '提交申请']
    },
    {
      step: '4',
      title: '现场核查',
      description: '相关部门进行现场检查和验收',
      icon: 'ri-search-eye-line',
      details: ['现场条件核查', '设备设施检验', '人员资格确认', '制度执行检查']
    },
    {
      step: '5',
      title: '审批发证',
      description: '通过审批后颁发相应的资质证书',
      icon: 'ri-award-line',
      details: ['审批决定作出', '证书制作', '证书领取', '公示公告']
    }
  ];

  const commonRequirements = [
    {
      category: '基础证件',
      icon: 'ri-file-shield-line',
      color: 'bg-blue-500',
      items: [
        '营业执照副本原件及复印件',
        '法定代表人身份证原件及复印件',
        '企业章程复印件（加盖公章）',
        '股东会决议或董事会决议',
        '组织机构代码证（三证合一前需要）'
      ]
    },
    {
      category: '场所材料',
      icon: 'ri-building-line',
      color: 'bg-green-500',
      items: [
        '经营场所房产证明或租赁合同',
        '场所平面图和地理位置图',
        '场所使用权证明文件',
        '环境影响评价报告（如需要）',
        '消防安全检查意见书（如需要）'
      ]
    },
    {
      category: '人员资质',
      icon: 'ri-team-line',
      color: 'bg-purple-500',
      items: [
        '相关专业技术人员资格证书',
        '管理人员身份证和学历证明',
        '从业人员健康证明（如需要）',
        '安全管理人员培训证书',
        '人员劳动合同或聘用协议'
      ]
    },
    {
      category: '专项材料',
      icon: 'ri-file-text-line',
      color: 'bg-orange-500',
      items: [
        '质量管理制度文件',
        '安全管理制度文件',
        '设备设施清单及合格证明',
        '技术方案或工艺流程',
        '应急预案和处置措施'
      ]
    }
  ];

  const detailedGuides = {
    'food-business': {
      title: '食品经营许可证详细指南',
      sections: [
        {
          title: '办事条件详解',
          icon: 'ri-checkbox-circle-line',
          content: [
            '【营业执照要求】已取得营业执照，且经营范围包含食品经营相关内容，如"餐饮服务"、"食品销售"等',
            '【经营场所条件】具有与经营的食品品种、数量相适应的经营场所，环境整洁，远离污染源，符合食品安全要求',
            '【设备设施要求】具有相应的消毒、更衣、盥洗、采光、照明、通风、防腐、防尘、防蝇、防鼠、防虫、洗涤以及处理废水、存放垃圾和废弃物的设备或设施',
            '【管理制度要求】有专职或兼职的食品安全管理人员和保证食品安全的规章制度',
            '【工艺流程要求】具有合理的设备布局和工艺流程，防止待加工食品与直接入口食品、原料与成品交叉污染',
            '【人员健康要求】从业人员需持有有效的健康证明，并定期进行健康检查'
          ]
        },
        {
          title: '申请材料清单',
          icon: 'ri-file-list-line',
          content: [
            '【申请书】《食品经营许可证》申请书，需加盖申请人公章',
            '【主体资格证明】营业执照或其他主体资格证明文件复印件',
            '【人员身份证明】法定代表人（负责人）、食品安全管理员的身份证明复印件',
            '【场所布局文件】与食品经营相适应的主要设备设施布局、操作流程等文件',
            '【管理制度文件】食品安全管理制度目录清单，包括从业人员健康管理、食品安全管理员管理、食品安全自查、食品经营过程与控制、场所及设施设备清洗消毒和维修保养、进货查验记录、食品贮存管理、废弃物处置、食品安全突发事件应急处置等制度',
            '【特殊设备材料】利用自动售货设备从事食品销售的，还需提交自动售货设备的产品合格证明、具体放置地点，经营者名称、住所、联系方式、食品经营许可证等信息的展示方法等材料',
            '【委托材料】申请人委托他人办理的，代理人应当提交授权委托书以及代理人的身份证明'
          ]
        },
        {
          title: '办理流程步骤',
          icon: 'ri-route-line',
          content: [
            '第1步：在线申请 - 登录"广东政务服务网"，搜索"食品经营许可证核发"，在线填写申请信息',
            '第2步：提交材料 - 上传所需的电子版材料，确保材料清晰完整',
            '第3步：材料受理 - 市场监管部门在5个工作日内完成材料受理审查',
            '第4步：现场核查 - 市场监管部门指派至少两名工作人员进行现场核查，核查经营场所、设备设施、人员配备等情况',
            '第5步：整改完善 - 如现场核查发现问题，申请人需按要求进行整改',
            '第6步：审批决定 - 市场监管部门自受理之日起20个工作日内作出是否准予行政许可的决定',
            '第7步：制证领取 - 准予许可的，在决定之日起10个工作日内制作并送达《食品经营许可证》'
          ]
        },
        {
          title: '现场核查要点',
          icon: 'ri-search-2-line',
          content: [
            '【场所环境检查】经营场所环境整洁、布局合理，与污染源保持规定距离',
            '【设施设备检查】食品处理区的设备设施配备齐全、运行正常，符合食品安全要求',
            '【仓储条件检查】食品贮存场所条件符合要求，具备必要的温度、湿度控制设施',
            '【人员管理检查】食品安全管理员到位，从业人员持有有效健康证',
            '【制度执行检查】各项食品安全管理制度建立完善，并能有效执行',
            '【记录档案检查】进货查验记录、从业人员健康管理档案等记录完整'
          ]
        },
        {
          title: '常见问题解答',
          icon: 'ri-question-line',
          content: [
            'Q: 小餐饮店是否需要办理食品经营许可证？A: 是的，从事餐饮服务都需要办理食品经营许可证',
            'Q: 网上销售食品需要什么特别要求？A: 需要在许可证上标注"网络经营"，并在网站显著位置公示许可证信息',
            'Q: 食品经营许可证的有效期是多长？A: 有效期为5年，到期前30个工作日内需申请延续',
            'Q: 变更经营地址需要重新办证吗？A: 是的，变更经营地址需要重新申请食品经营许可证',
            'Q: 从业人员健康证多久需要更新？A: 健康证有效期为1年，到期前需重新体检办理'
          ]
        },
        {
          title: '注意事项提醒',
          icon: 'ri-alarm-warning-line',
          content: [
            '食品经营许可证不得伪造、涂改、倒卖、出租、出借或者以其他形式非法转让',
            '许可证载明的许可事项发生变化的，食品经营者应当在变化后10个工作日内向原发证的食品药品监督管理部门申请变更经营许可',
            '食品经营者应当在经营场所的显著位置悬挂或者摆放食品经营许可证正本',
            '食品经营者应当建立食品安全自查制度，定期对食品安全状况进行检查评价',
            '发生食品安全事故时，应当立即停止食品经营活动，并向所在地县级人民政府食品药品监督管理部门报告'
          ]
        }
      ]
    },
    'medical-device': {
      title: '医疗器械经营许可证详细指南',
      sections: [
        {
          title: '分类管理说明',
          icon: 'ri-health-book-line',
          content: [
            '【第一类医疗器械】风险程度低，实施常规管理可以保证其安全、有效的医疗器械，无需许可和备案',
            '【第二类医疗器械】具有中度风险，需要严格控制管理以保证其安全有效的医疗器械，实行产品注册和经营备案管理',
            '【第三类医疗器械】具有较高风险，需要采取特别措施严格控制管理以保证其安全有效的医疗器械，实行产品注册和经营许可管理',
            '【许可范围】第三类医疗器械经营需要取得《医疗器械经营许可证》',
            '【备案要求】第二类医疗器械经营需要进行经营备案'
          ]
        },
        {
          title: '第三类器械经营条件',
          icon: 'ri-shield-check-line',
          content: [
            '【主体资格要求】已取得营业执照，且经营范围包含"第三类医疗器械经营"或类似内容',
            '【经营场所要求】具有与经营规模和范围相适应的经营场所和库房，并具备符合医疗器械产品特性要求的贮存设施设备',
            '【温湿度控制】贮存设施设备应当具备温湿度监控设备，确保医疗器械在适宜的环境中贮存',
            '【质量管理制度】具有与经营规模和范围相适应的质量管理制度，包括采购、验收、仓储、销售、追溯等制度',
            '【专业人员要求】具有与经营规模和范围相适应的质量管理人员，质量管理人员应当具有国家认可的相关专业学历或者职称',
            '【信息系统要求】具备可供追溯的计算机信息管理系统，能够对医疗器械的经营过程进行记录和追溯'
          ]
        },
        {
          title: '申请材料详单',
          icon: 'ri-file-copy-line',
          content: [
            '【申请表】《医疗器械经营许可证》申请表，申请人签字并加盖公章',
            '【主体证明】营业执照副本复印件',
            '【人员证明】法定代表人、企业负责人、质量负责人的身份证明、学历或者职称证明复印件',
            '【场所证明】经营场所、库房的地理位置图、平面图、房屋产权证明文件或者租赁协议复印件',
            '【设备清单】经营设施、设备目录，包括计算机信息管理系统、温湿度监控设备等',
            '【质量制度】产品质量管理制度目录，应当包括质量管理机构或者质量管理人员的职责、采购管理制度、进货查验记录制度、仓储管理制度、出库复核制度、质量管理制度、不合格医疗器械处理制度、医疗器械召回制度、售后服务制度、不良事件监测和报告制度、经营质量管理制度执行情况考核制度等',
            '【委托证明】经办人授权证明及身份证明'
          ]
        },
        {
          title: '办理流程指引',
          icon: 'ri-flow-chart',
          content: [
            '第1步：系统登录 - 登录国家药品监督管理局《食品药品监管信息平台》或省级药监部门网站',
            '第2步：在线申报 - 填写《医疗器械经营许可证》申请表，上传相关材料',
            '第3步：材料提交 - 向所在地市级药品监督管理局窗口提交纸质材料（如需要）',
            '第4步：受理审查 - 药品监督管理部门对申请材料进行形式审查',
            '第5步：现场核查 - 组织专家或工作人员进行现场核查，重点检查经营场所、仓储条件、人员配备、质量管理制度执行情况',
            '第6步：技术审查 - 对申请材料和现场核查报告进行技术审查',
            '第7步：审批决定 - 省级药品监督管理局作出审批决定',
            '第8步：证书制发 - 审批通过后制作许可证，可选择窗口领取或邮寄'
          ]
        },
        {
          title: '现场核查要点',
          icon: 'ri-eye-line',
          content: [
            '【场所条件核查】经营场所和库房面积、布局是否符合要求，是否与申报材料一致',
            '【仓储设施核查】温湿度监控设备是否配备完善，贮存条件是否符合医疗器械要求',
            '【人员资质核查】质量管理人员是否具备相应专业背景，是否在岗履职',
            '【制度执行核查】各项质量管理制度是否建立健全，是否能够有效执行',
            '【信息系统核查】计算机信息管理系统是否能够实现进销存管理和产品追溯',
            '【记录档案核查】相关记录和档案是否完整、真实、可追溯'
          ]
        },
        {
          title: '第二类器械备案',
          icon: 'ri-bookmark-line',
          content: [
            '【备案条件】经营第二类医疗器械的，应当向所在地设区的市级药品监督管理部门备案',
            '【备案材料】营业执照复印件、法定代表人身份证明、质量管理人员身份证明和学历证明、经营场所证明',
            '【备案流程】通过医疗器械经营备案信息系统提交备案信息，获得备案凭证',
            '【备案时限】开始经营前向药品监督管理部门备案',
            '【变更备案】备案信息发生变化的，应当及时变更备案信息'
          ]
        }
      ]
    },
    'import-export': {
      title: '进出口经营权备案详细指南',
      sections: [
        {
          title: '办事条件详解',
          icon: 'ri-ship-line',
          content: [
            '【营业执照要求】已取得营业执照，且经营范围包含"货物进出口"或"技术进出口"相关项目',
            '【银行开户要求】已开立银行基本账户，用于外汇收支和结算',
            '【电子口岸准备】企业法定代表人和操作员需办理电子口岸IC卡，用于报关操作',
            '【场所条件】具有固定的经营场所，符合海关监管要求',
            '【人员资质】指定专门的进出口业务操作人员，熟悉相关法规'
          ]
        },
        {
          title: '所需材料清单',
          icon: 'ri-file-list-3-line',
          content: [
            '【基础证件】营业执照副本复印件（加盖公章）',
            '【人员材料】法定代表人身份证复印件、操作员身份证复印件',
            '【企业文件】公司章程复印件（加盖公章）',
            '【场所证明】经营场所证明（如租赁合同或房产证复印件）',
            '【银行资料】银行开户许可证复印件或基本存款账户信息',
            '【授权文件】如委托他人办理，需提供授权委托书和经办人身份证'
          ]
        },
        {
          title: '办理流程与部门',
          icon: 'ri-route-line',
          content: [
            '第1步：工商经营范围变更 - 若营业执照经营范围未包含进出口项目，需先在工商部门增加"货物进出口、技术进出口"等相关内容',
            '第2步：对外贸易经营者备案登记 - 通过"全国一体化政务服务平台"或地方商务局窗口办理，获取《对外贸易经营者备案登记表》',
            '第3步：海关备案 - 登录"中国国际贸易单一窗口"平台，填写信息，向海关申请办理进出口货物收发货人注册登记，获取《报关单位注册登记证书》（现已全面启用电子证书）',
            '第4步：外汇管理局名录登记 - 通过"国家外汇管理局数字外管平台（ASOne系统）"办理"货物贸易外汇收支企业名录"登记',
            '第5步：电子口岸入网申请 - 到所在地电子口岸数据中心分中心办理IC卡、软件等，用于报关操作',
            '第6步：出口退税备案 - 有出口业务时，向主管税务机关办理出口退税资格备案（可选）'
          ]
        },
        {
          title: '各部门办理要点',
          icon: 'ri-government-line',
          content: [
            '【商务局备案】重点审核企业主体资格和经营能力，备案成功后可获得进出口经营资格',
            '【海关注册登记】审核企业经营场所、管理制度、人员配备等，确保符合海关监管要求',
            '【外汇局名录登记】主要审核企业外汇收支能力和合规性，登记后可正常办理外汇业务',
            '【电子口岸申请】需要法定代表人和操作员本人到场办理，确保IC卡使用安全',
            '【税务退税备案】审核企业出口业务真实性，备案后可享受出口退税政策'
          ]
        },
        {
          title: '办理时间与费用',
          icon: 'ri-time-line',
          content: [
            '【总体时限】各项备案登记手续办理时间不一，材料齐全且顺利的情况下，全流程约需7-12个工作日',
            '【商务局备案】1-3个工作日完成',
            '【海关注册登记】3-5个工作日完成',
            '【外汇局名录登记】1-2个工作日完成',
            '【电子口岸申请】1-2个工作日完成',
            '【政府费用】政府各部门不收取办理费用，均为免费服务',
            '【其他费用】可能产生的费用主要是IC卡工本费、软件使用费等，一般在几百元左右'
          ]
        },
        {
          title: '注意事项提醒',
          icon: 'ri-alert-line',
          content: [
            '进出口经营权备案完成后，企业应建立健全进出口业务管理制度，确保合规经营',
            '海关、外汇等部门的备案信息发生变化时，应及时办理变更手续',
            '电子口岸IC卡要妥善保管，遗失后应立即挂失并重新申请',
            '开展进出口业务前，还需了解相关商品的进出口政策和检验检疫要求',
            '定期关注海关、外汇管理等相关政策变化，确保业务合规'
          ]
        }
      ]
    },
    'prepackaged-food': {
      title: '预包装食品经营备案详细指南',
      sections: [
        {
          title: '备案制度说明',
          icon: 'ri-information-line',
          content: [
            '【政策变化】仅销售预包装食品的经营者，由原来的"许可制"改为"备案制"，大大简化了办理流程',
            '【适用范围】仅适用于销售预包装食品的经营者，不包括现制现售食品、散装食品等',
            '【预包装食品定义】指预先定量包装或者制作在包装材料、容器中的食品，包括预先定量包装以及预先定量制作在包装材料和容器中并且在一定量限范围内具有统一的质量或体积标识的食品',
            '【与许可制区别】备案制相比许可制，办理程序更简化，时间更短，要求相对宽松'
          ]
        },
        {
          title: '办事条件详解',
          icon: 'ri-checkbox-circle-line',
          content: [
            '【营业执照要求】已取得营业执照，且经营范围必须含有"食品销售（仅销售预包装食品）"',
            '【经营场所要求】具备合法的经营场所，环境干净整洁，符合食品安全基本要求',
            '【人员配备要求】配备食品安全管理员和负责人，了解食品安全基本知识',
            '【产品范围限制】只能销售预包装食品，不得从事食品生产、现制现售等其他食品经营活动',
            '【网络销售要求】如进行网络销售，需在网站显著位置公示备案信息'
          ]
        },
        {
          title: '所需材料清单',
          icon: 'ri-file-text-line',
          content: [
            '【营业执照材料】营业执照副本原件及复印件（复印件加盖公章）',
            '【法人股东材料】法定代表人和股东身份证原件及复印件',
            '【管理人员材料】食品安全管理员和负责人身份证复印件',
            '【申请表格】预包装食品经营备案申请表（现场填写或网上下载）',
            '【企业印章】公章（用于在相关材料上盖章确认）',
            '【其他材料】根据具体情况可能需要的其他证明材料'
          ]
        },
        {
          title: '办理流程步骤',
          icon: 'ri-flow-chart',
          content: [
            '第1步：网上填报 - 通过相关行政审批系统或市场监管部门官网在线填报备案信息',
            '第2步：材料上传 - 按系统要求上传营业执照、身份证等相关材料的电子版',
            '第3步：信息确认 - 仔细核对填报的各项信息，确保准确无误后提交',
            '第4步：系统审核 - 市场监督管理部门会对提交的备案信息进行审核',
            '第5步：备案完成 - 审核通过后，系统会生成备案号和备案凭证',
            '第6步：信息公示 - 备案信息会通过相关平台公示，企业可自行查询',
            '第7步：下载凭证 - 企业可登录系统下载打印备案凭证'
          ]
        },
        {
          title: '备案后管理要求',
          icon: 'ri-settings-line',
          content: [
            '【凭证悬挂】应当在经营场所显著位置悬挂或摆放备案凭证',
            '【进货查验】建立食品进货查验记录制度，查验供货者的许可证和食品出厂检验合格证或其他合格证明',
            '【贮存管理】按照食品标签标示的警示标志、警示说明或注意事项的要求贮存和销售食品',
            '【信息变更】备案信息发生变化的，应当及时向原备案部门申请变更备案',
            '【自查制度】定期对食品安全状况进行检查评价，发现问题及时整改'
          ]
        },
        {
          title: '办理时间与优势',
          icon: 'ri-timer-line',
          content: [
            '【办理时限】备案手续相对简单，办理速度较快，通常3-5天内即可完成',
            '【费用优势】自行办理无任何官方费用，完全免费',
            '【流程简化】相比食品经营许可证，无需现场核查环节，大大节省时间',
            '【即时生效】备案完成即可开展经营活动，无需等待许可证制作',
            '【网上办理】全程可通过网络办理，无需跑腿'
          ]
        },
        {
          title: '常见问题解答',
          icon: 'ri-question-line',
          content: [
            'Q: 哪些食品属于预包装食品？A: 包括各种包装的饼干、糖果、饮料、罐头、调料等，有完整包装和标签的食品',
            'Q: 可以同时销售散装食品吗？A: 不可以，备案制仅适用于销售预包装食品，销售散装食品需办理食品经营许可证',
            'Q: 备案有效期是多长？A: 备案长期有效，但备案信息发生变化时需及时变更',
            'Q: 网络销售预包装食品需要特别注意什么？A: 需在网站显著位置公示备案信息，确保消费者能够查看',
            'Q: 备案后还需要办理其他手续吗？A: 如涉及进口食品、特殊食品等，可能需要办理其他相关手续'
          ]
        }
      ]
    },
    'construction-qualification': {
      title: '建筑业资质详细指南',
      sections: [
        {
          title: '资质分类体系',
          icon: 'ri-building-4-line',
          content: [
            '【施工总承包资质】包括建筑工程、公路工程、铁路工程、港口与航道工程、水利水电工程、电力工程、矿山工程、冶金工程、石油化工工程、市政公用工程、通信工程、机电工程等12个类别',
            '【专业承包资质】包括地基基础工程、起重设备安装工程、预拌混凝土、模板脚手架、建筑装修装饰工程、建筑幕墙工程、古建筑工程、城市及道路照明工程等36个类别',
            '【施工劳务资质】不分类别和等级',
            '【工程监理资质】包括房屋建筑工程、冶炼工程、化工石油工程、水利水电工程、机电工程等14个类别'
          ]
        },
        {
          title: '申请基本条件',
          icon: 'ri-check-line',
          content: [
            '【企业资产条件】净资产达到规定标准，如建筑工程施工总承包一级资质要求净资产1亿元以上',
            '【主要人员条件】建造师、工程师、施工现场管理人员、技术工人等达到规定数量和资格要求',
            '【工程业绩条件】近5年承担过相应等级和类别的工程施工，工程质量合格',
            '【技术装备条件】具有与承包工程范围相适应的施工机械和质量检测设备',
            '【管理体系条件】具有完善的质量管理体系、安全生产管理体系和环境管理体系'
          ]
        },
        {
          title: '申请材料要求',
          icon: 'ri-file-list-2-line',
          content: [
            '【基础材料】建筑业企业资质申请表、企业法人营业执照、企业章程、验资报告',
            '【人员材料】企业主要人员身份证明、职业资格证书、职称证书、社会保险证明',
            '【业绩材料】工程合同、工程竣工验收单（备案表）、施工图纸、工程照片',
            '【设备材料】主要设备购置发票、设备检测报告',
            '【管理材料】质量管理体系认证证书、安全生产许可证、环境管理体系认证证书'
          ]
        }
      ]
    },
    'transport-license': {
      title: '道路运输经营许可证详细指南',
      sections: [
        {
          title: '许可证类型',
          icon: 'ri-truck-line',
          content: [
            '【道路货物运输经营许可证】从事货物运输经营的许可证明',
            '【道路旅客运输经营许可证】从事客运服务的许可证明',
            '【道路危险货物运输许可证】从事危险品运输的专项许可',
            '【机动车维修经营许可证】从事机动车维修业务的许可',
            '【机动车驾驶员培训许可证】从事驾驶培训业务的许可'
          ]
        },
        {
          title: '申请基本条件',
          icon: 'ri-checkbox-line',
          content: [
            '【车辆条件】拥有或租赁符合规定的运输车辆，车辆技术等级达到二级以上',
            '【驾驶员条件】有与经营业务相适应的驾驶人员，驾驶员须取得相应从业资格证',
            '【安全管理条件】有健全的安全生产管理制度，有相应的安全生产管理机构和人员',
            '【经营场所条件】有固定的经营场所以及与经营业务相适应的停车场地',
            '【资金保证条件】有相应的资金保证，货运企业须按规定投保承运人责任险'
          ]
        }
      ]
    },
    'education-license': {
      title: '民办学校办学许可证详细指南',
      sections: [
        {
          title: '办学类型分类',
          icon: 'ri-school-line',
          content: [
            '【学历教育类】民办幼儿园、民办中小学、民办高等学校等',
            '【非学历教育类】民办培训机构、民办继续教育机构等',
            '【职业教育类】民办职业学校、民办技工学校等',
            '【其他教育类】民办教育咨询机构、民办家政服务培训等'
          ]
        },
        {
          title: '申办基本条件',
          icon: 'ri-building-line',
          content: [
            '【办学场所】有符合安全条件的固定场所，建筑面积不少于规定标准',
            '【师资队伍】有与办学规模相适应的专兼职教师队伍，教师须具备相应资格',
            '【教学设备】有与办学内容相适应的教学设备和图书资料',
            '【办学资金】有稳定的办学经费来源，注册资金不少于规定数额',
            '【管理制度】有完善的办学章程和各项管理制度'
          ]
        }
      ]
    },
    'internet-culture': {
      title: '网络文化经营许可证详细指南',
      sections: [
        {
          title: '经营范围分类',
          icon: 'ri-computer-line',
          content: [
            '【网络游戏】网络游戏运营、网络游戏虚拟货币发行、网络游戏虚拟货币交易',
            '【网络音乐】音乐网站、音乐播放平台、音乐下载服务',
            '【网络演出】网络直播、网络表演、网络演艺',
            '【网络表演】网络直播平台、网络秀场、网络K歌',
            '【网络艺术品】网络艺术品拍卖、网络艺术品交易',
            '【网络动漫】动漫网站、动漫游戏、动漫产品'
          ]
        },
        {
          title: '申请基本条件',
          icon: 'ri-shield-check-line',
          content: [
            '【注册资本】公司注册资本不少于100万元人民币',
            '【专业人员】有适应经营活动需要的专业人员，不少于8名',
            '【技术设备】有必要的设备、工作场所以及相应的经营管理技术措施',
            '【内容管理】有确定的网络文化产品范围和明确的网络文化产品内容管理制度',
            '【安全措施】有完善的信息安全管理制度和技术保障措施'
          ]
        }
      ]
    }
  };

  // 如果选择了详细指南，显示指南内容
  if (selectedGuide && detailedGuides[selectedGuide as keyof typeof detailedGuides]) {
    const guide = detailedGuides[selectedGuide as keyof typeof detailedGuides];
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 to-indigo-100">
        {/* Header */}
        <header className="bg-white shadow-sm">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <button 
                  onClick={() => setSelectedGuide(null)}
                  className="text-purple-600 hover:text-purple-800 cursor-pointer"
                >
                  <i className="ri-arrow-left-line text-xl"></i>
                </button>
                <div>
                  <h1 className="text-3xl font-bold text-gray-900">{guide.title}</h1>
                  <p className="text-lg text-gray-600 mt-2">专业资质许可办理指南</p>
                </div>
              </div>
              <div className="flex items-center space-x-4">
                <button className="bg-purple-600 text-white px-6 py-2 rounded-lg hover:bg-purple-700 transition-colors cursor-pointer whitespace-nowrap">
                  收藏指南
                </button>
                <button className="border border-purple-600 text-purple-600 px-6 py-2 rounded-lg hover:bg-purple-50 transition-colors cursor-pointer whitespace-nowrap">
                  打印指南
                </button>
              </div>
            </div>
          </div>
        </header>

        {/* Guide Content */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
            {/* Navigation Sidebar */}
            <div className="lg:col-span-1">
              <div className="bg-white rounded-xl shadow-lg p-6 sticky top-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">指南目录</h3>
                <nav className="space-y-2">
                  {guide.sections.map((section, index) => (
                    <a
                      key={index}
                      href={`#section-${index}`}
                      className="flex items-center p-3 text-gray-700 hover:bg-purple-50 hover:text-purple-700 rounded-lg transition-colors cursor-pointer"
                    >
                      <i className={`${section.icon} mr-3 text-purple-600`}></i>
                      <span className="text-sm font-medium">{section.title}</span>
                    </a>
                  ))}
                </nav>
              </div>
            </div>

            {/* Main Content */}
            <div className="lg:col-span-3">
              <div className="space-y-8">
                {guide.sections.map((section, index) => (
                  <div
                    key={index}
                    id={`section-${index}`}
                    className="bg-white rounded-xl shadow-lg overflow-hidden"
                  >
                    <div className="bg-gradient-to-r from-purple-500 to-indigo-600 px-6 py-4">
                      <div className="flex items-center">
                        <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center mr-4">
                          <i className={`${section.icon} text-white text-xl`}></i>
                        </div>
                        <h3 className="text-xl font-bold text-white">{section.title}</h3>
                      </div>
                    </div>
                    <div className="p-6">
                      <div className="space-y-4">
                        {section.content.map((item, itemIndex) => (
                          <div key={itemIndex} className="flex items-start">
                            <div className="w-2 h-2 bg-purple-500 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                            <p className="text-gray-700 leading-relaxed">{item}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Action Buttons */}
              <div className="mt-8 bg-gradient-to-r from-purple-500 to-indigo-600 rounded-xl p-6 text-white">
                <div className="text-center">
                  <h3 className="text-2xl font-bold mb-4">需要专业协助？</h3>
                  <p className="text-purple-100 mb-6">我们的专业顾问团队为您提供资质许可申请的全程指导</p>
                  <div className="flex flex-col sm:flex-row justify-center space-y-4 sm:space-y-0 sm:space-x-4">
                    <button className="bg-white text-purple-600 px-6 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors cursor-pointer whitespace-nowrap">
                      在线咨询顾问
                    </button>
                    <button className="border-2 border-white text-white px-6 py-3 rounded-lg font-semibold hover:bg-white hover:text-purple-600 transition-colors cursor-pointer whitespace-nowrap">
                      预约申请服务
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 to-indigo-100">
      {/* Header */}
      <header className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <button 
                onClick={() => window.REACT_APP_NAVIGATE('/')}
                className="text-purple-600 hover:text-purple-800 cursor-pointer"
              >
                <i className="ri-arrow-left-line text-xl"></i>
              </button>
              <div>
                <h1 className="text-3xl font-bold text-gray-900">资质许可培训</h1>
                <p className="text-lg text-gray-600 mt-2">各类行业资质申请与许可证办理指导</p>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <div className="bg-purple-100 px-4 py-2 rounded-full">
                <span className="text-purple-800 font-medium">学习进度: 0%</span>
              </div>
              <button className="bg-purple-600 text-white px-6 py-2 rounded-lg hover:bg-purple-700 transition-colors cursor-pointer whitespace-nowrap">
                开始学习
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative py-16 overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: `url('https://public.readdy.ai/ai/img_res/96f1688049388b8fae77ac032bad1561.jpg')`
          }}
        >
          <div className="absolute inset-0 bg-purple-900/70"></div>
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold text-white mb-6">
            专业资质许可服务
          </h2>
          <p className="text-xl text-purple-100 mb-8 max-w-3xl mx-auto">
            为企业提供全方位的行业资质申请和许可证办理服务，确保合规经营
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl mx-auto">
            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4 text-white">
              <div className="text-2xl font-bold mb-2">专业团队</div>
              <div className="text-purple-100">经验丰富</div>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4 text-white">
              <div className="text-2xl font-bold mb-2">高效办理</div>
              <div className="text-purple-100">快速通过</div>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4 text-white">
              <div className="text-2xl font-bold mb-2">全程跟踪</div>
              <div className="text-purple-100">贴心服务</div>
            </div>
          </div>
        </div>
      </section>

      {/* Permit Types */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h3 className="text-3xl font-bold text-gray-900 mb-4">主要资质许可类型</h3>
            <p className="text-lg text-gray-600">覆盖各行业常见的资质证书和经营许可</p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {permitTypes.map((permit) => (
              <div key={permit.id} className="bg-white rounded-xl shadow-lg hover:shadow-xl transition-shadow duration-300 overflow-hidden group">
                <div className="p-6">
                  <div className="flex items-center mb-4">
                    <div className="w-14 h-14 bg-purple-100 rounded-lg flex items-center justify-center mr-4">
                      <i className={`${permit.icon} text-purple-600 text-2xl`}></i>
                    </div>
                    <div>
                      <h4 className="text-xl font-bold text-gray-900">{permit.title}</h4>
                      <p className="text-gray-600 text-sm">{permit.description}</p>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4 mb-6">
                    <div>
                      <h5 className="font-semibold text-gray-900 mb-1">办理时限</h5>
                      <p className="text-purple-600 font-medium text-sm">{permit.timeframe}</p>
                    </div>
                    <div>
                      <h5 className="font-semibold text-gray-900 mb-1">费用标准</h5>
                      <p className="text-purple-600 font-medium text-sm">{permit.price}</p>
                    </div>
                  </div>
                  
                  <div className="mb-4">
                    <h5 className="font-semibold text-gray-900 mb-2">适用范围</h5>
                    <div className="flex flex-wrap gap-2">
                      {permit.scope.map((scope, index) => (
                        <span 
                          key={index}
                          className="bg-purple-50 text-purple-700 px-3 py-1 rounded-full text-sm"
                        >
                          {scope}
                        </span>
                      ))}
                    </div>
                  </div>

                  <div className="mb-6">
                    <h5 className="font-semibold text-gray-900 mb-2">主要条件</h5>
                    <div className="space-y-1">
                      {permit.conditions.map((condition, index) => (
                        <div key={index} className="flex items-center text-sm text-gray-600">
                          <div className="w-1.5 h-1.5 bg-purple-500 rounded-full mr-2"></div>
                          {condition}
                        </div>
                      ))}
                    </div>
                  </div>
                  
                  <button 
                    onClick={() => setSelectedGuide(permit.id)}
                    className="w-full bg-purple-600 text-white py-3 rounded-lg hover:bg-purple-700 transition-colors cursor-pointer whitespace-nowrap group-hover:bg-purple-700"
                  >
                    查看详细指南
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Application Process */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h3 className="text-3xl font-bold text-gray-900 mb-4">资质申请流程</h3>
            <p className="text-lg text-gray-600">标准化的申请流程，确保高效办理</p>
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
            {applicationProcess.map((process, index) => (
              <div key={process.step} className="text-center">
                <div className="relative mb-6">
                  <div className="w-20 h-20 bg-purple-600 rounded-full flex items-center justify-center mx-auto mb-4">
                    <i className={`${process.icon} text-white text-2xl`}></i>
                  </div>
                  <div className="absolute -top-2 -right-2 w-8 h-8 bg-purple-800 rounded-full flex items-center justify-center">
                    <span className="text-white font-bold text-sm">{process.step}</span>
                  </div>
                  {index < applicationProcess.length - 1 && (
                    <div className="hidden lg:block absolute top-10 left-full w-full h-0.5 bg-purple-200 -ml-10"></div>
                  )}
                </div>
                <h4 className="text-lg font-semibold text-gray-900 mb-2">{process.title}</h4>
                <p className="text-gray-600 mb-4">{process.description}</p>
                <div className="space-y-1">
                  {process.details.map((detail, detailIndex) => (
                    <div key={detailIndex} className="text-sm text-gray-500">
                      • {detail}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Common Requirements */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h3 className="text-3xl font-bold text-gray-900 mb-4">通用申请材料</h3>
            <p className="text-lg text-gray-600">大部分资质申请都需要的基础材料</p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {commonRequirements.map((category, index) => (
              <div key={index} className="bg-white rounded-xl shadow-lg overflow-hidden">
                <div className={`${category.color} p-4`}>
                  <div className="flex items-center justify-center text-white">
                    <i className={`${category.icon} text-2xl mr-3`}></i>
                    <h4 className="text-lg font-bold">{category.category}</h4>
                  </div>
                </div>
                <div className="p-6">
                  <ul className="space-y-3">
                    {category.items.map((item, itemIndex) => (
                      <li key={itemIndex} className="flex items-start">
                        <div className="w-2 h-2 bg-purple-500 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                        <span className="text-gray-700 text-sm">{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>
          
          <div className="mt-8 bg-amber-50 border-l-4 border-amber-500 p-6 rounded-r-lg">
            <div className="flex items-center mb-2">
              <i className="ri-lightbulb-line text-amber-600 text-xl mr-2"></i>
              <h4 className="text-lg font-semibold text-amber-800">材料准备提醒</h4>
            </div>
            <p className="text-amber-700">
              不同类型的资质许可所需材料会有差异，建议在申请前详细了解具体要求。
              所有材料都需要提供原件及复印件，复印件需加盖公章。部分材料需要经过公证或认证。
            </p>
          </div>
        </div>
      </section>

      {/* Key Features */}
      <section className="py-16 bg-purple-600">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h3 className="text-3xl font-bold text-white mb-4">专业服务优势</h3>
            <p className="text-xl text-purple-100">选择我们的资质许可服务的理由</p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 text-center">
              <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-user-star-line text-white text-2xl"></i>
              </div>
              <h4 className="text-lg font-bold text-white mb-2">专业团队</h4>
              <p className="text-purple-100 text-sm">拥有丰富经验的专业顾问团队</p>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 text-center">
              <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-time-line text-white text-2xl"></i>
              </div>
              <h4 className="text-lg font-bold text-white mb-2">高效办理</h4>
              <p className="text-purple-100 text-sm">快速响应，缩短办理周期</p>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 text-center">
              <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-shield-check-line text-white text-2xl"></i>
              </div>
              <h4 className="text-lg font-bold text-white mb-2">成功保障</h4>
              <p className="text-purple-100 text-sm">丰富经验确保申请成功率</p>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 text-center">
              <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-customer-service-line text-white text-2xl"></i>
              </div>
              <h4 className="text-lg font-bold text-white mb-2">全程服务</h4>
              <p className="text-purple-100 text-sm">从咨询到获证的全程跟踪服务</p>
            </div>
          </div>
        </div>
      </section>

      {/* Important Notes */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h3 className="text-3xl font-bold text-gray-900 mb-4">重要注意事项</h3>
            <p className="text-lg text-gray-600">申请资质许可时需要特别关注的要点</p>
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <div className="space-y-6">
              <div className="bg-red-50 border-l-4 border-red-500 p-6 rounded-r-lg">
                <div className="flex items-center mb-3">
                  <i className="ri-time-line text-red-600 text-xl mr-2"></i>
                  <h4 className="text-lg font-semibold text-red-800">时限要求</h4>
                </div>
                <p className="text-red-700 text-sm">
                  各类资质许可都有严格的申请时限和有效期要求，过期未申请或未延续将面临处罚。
                  建议企业提前规划，在资质到期前3个月开始准备延续申请。
                </p>
              </div>
              
              <div className="bg-orange-50 border-l-4 border-orange-500 p-6 rounded-r-lg">
                <div className="flex items-center mb-3">
                  <i className="ri-file-warning-line text-orange-600 text-xl mr-2"></i>
                  <h4 className="text-lg font-semibold text-orange-800">材料真实性</h4>
                </div>
                <p className="text-orange-700 text-sm">
                  所有申请材料必须真实有效，不得提供虚假信息。一经发现提供虚假材料，
                  将被列入黑名单，影响企业信用记录，严重的可能面临法律责任。
                </p>
              </div>
            </div>
            
            <div className="space-y-6">
              <div className="bg-blue-50 border-l-4 border-blue-500 p-6 rounded-r-lg">
                <div className="flex items-center mb-3">
                  <i className="ri-shield-line text-blue-600 text-xl mr-2"></i>
                  <h4 className="text-lg font-semibold text-blue-800">安全管理</h4>
                </div>
                <p className="text-blue-700 text-sm">
                  获得资质许可后，企业必须严格按照许可范围经营，建立健全相应的管理制度，
                  定期进行安全检查，确保持续符合许可条件。
                </p>
              </div>
              
              <div className="bg-purple-50 border-l-4 border-purple-500 p-6 rounded-r-lg">
                <div className="flex items-center mb-3">
                  <i className="ri-refresh-line text-purple-600 text-xl mr-2"></i>
                  <h4 className="text-lg font-semibold text-purple-800">变更申报</h4>
                </div>
                <p className="text-purple-700 text-sm">
                  企业信息发生变化时（如地址、法人、经营范围等），应及时向相关部门申报变更，
                  保持资质证书信息与实际情况一致。
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gray-900">
        <div className="max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8">
          <h3 className="text-3xl font-bold text-white mb-4">
            专业资质许可服务，助力企业合规发展
          </h3>
          <p className="text-xl text-gray-300 mb-8">
            让专业的事交给专业的人，我们为您提供一站式资质许可解决方案
          </p>
          <div className="flex flex-col sm:flex-row justify-center space-y-4 sm:space-y-0 sm:space-x-4">
            <button className="bg-purple-600 text-white px-8 py-4 rounded-lg font-semibold hover:bg-purple-700 transition-colors cursor-pointer whitespace-nowrap">
              免费咨询评估
            </button>
            <button className="border-2 border-white text-white px-8 py-4 rounded-lg font-semibold hover:bg-white hover:text-gray-900 transition-colors cursor-pointer whitespace-nowrap">
              获取申请方案
            </button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-800 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div>
              <h4 className="text-lg font-semibold mb-4">联系我们</h4>
              <div className="space-y-2 text-gray-300">
                <p>电话: 020-1234-5678</p>
                <p>邮箱: license@example.com</p>
                <p>地址: 广州市天河区xxx路123号</p>
              </div>
            </div>
            <div>
              <h4 className="text-lg font-semibold mb-4">快速链接</h4>
              <div className="space-y-2 text-gray-300">
                <p className="cursor-pointer hover:text-white" onClick={() => window.REACT_APP_NAVIGATE('/')}>返回首页</p>
                <p>申请流程</p>
                <p>费用标准</p>
                <p>常见问题</p>
              </div>
            </div>
            <div>
              <h4 className="text-lg font-semibold mb-4">热门资质</h4>
              <div className="space-y-2 text-gray-300">
                <p>食品经营许可证</p>
                <p>医疗器械许可证</p>
                <p>建筑业资质</p>
                <p>道路运输许可证</p>
              </div>
            </div>
            <div>
              <h4 className="text-lg font-semibold mb-4">增值服务</h4>
              <div className="space-y-2 text-gray-300">
                <p>资质代办</p>
                <p>年检服务</p>
                <p>变更服务</p>
                <p><a href="https://readdy.ai/?origin=logo" className="hover:text-white transition-colors">Made with Readdy</a></p>
              </div>
            </div>
          </div>
          <div className="border-t border-gray-700 mt-8 pt-8 text-center text-gray-400">
            <p>&copy; 2024 企服培训平台. 保留所有权利.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
